import React from 'react';
import styled, { css } from 'styled-components';
import LazyImage from '../lazy-image';
import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed';
import Arrow from '../../images/svg/arrow-icon.svg';
import { Link } from 'gatsby';
import parseStoryblokLink from '../../utils/parse-storyblok-link';
import type { StoryblokLink } from '../../types/case-study';
import mq from '../../utils/mq';
import CtaWithArrow from '../cta-with-arrow';

const CaseStudiesSliderItemContainer = styled.div`
  flex: 0 0 100%;
  min-width: 0;
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-radius: var(--Radius-Radius-3, 16px);
  grid-gap: 0px;
  position: relative;
  overflow: hidden;
  &.light-theme {
    background: #161616;
  }
  ${mq.mobile(css`
    display: flex;
    flex-direction: column-reverse;
  `)};
`;

const CaseStudiesSliderItemLeftContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-direction: column;
  padding: 72px;
  z-index: 2;
  background: #1d1d1d;
  ${mq.mobile(css`
    padding: 32px;
  `)};
`;

const CaseStudiesSliderItemRightContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  z-index: 1;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  overflow: hidden;
  ${mq.mobile(css`
    position: relative;
    width: 100%;
    aspect-ratio: 16/6;
  `)};
`;

const ImageContainer = styled(Link)`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Title = styled.h3`
  color: #fff;
  font-size: 27px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  margin-bottom: 16px;
`;

const Description = styled.p`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
  margin-bottom: 24px;
  color: var(--Foreground-fg-secondary-inverse, #a8a8a8);
`;

const InfoItemsContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 20px;
  padding-bottom: 46px;
`;

const InfoItem = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  gap: 2px;
`;

const InfoItemTitle = styled.h4`
  color: #fff;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
`;

const InfoItemDescription = styled.p`
  color: var(--Foreground-fg-secondary-inverse, #a8a8a8);
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
`;

export default function CaseStudiesSliderItem({
  title,
  description,
  image,
  ctaLabel,
  ctaLink,
  infoItems,
  lightTheme
}: {
  title: string;
  description: string;
  image: string;
  ctaLabel: string;
  ctaLink: string | StoryblokLink | null;
  infoItems: any[];
  lightTheme?: boolean;
}) {
  const resolvedCtaLink =
    typeof ctaLink === 'string'
      ? ctaLink
      : ctaLink && typeof ctaLink === 'object'
      ? parseStoryblokLink(ctaLink)
      : null;

  return (
    <CaseStudiesSliderItemContainer
      className={lightTheme ? 'embla__slide light-theme' : 'embla__slide'}
    >
      <CaseStudiesSliderItemLeftContent>
        <Title>{title}</Title>
        <Description>{description}</Description>
        <InfoItemsContainer>
          {infoItems &&
            infoItems?.length > 0 &&
            infoItems.map((infoItem, index) => (
              <InfoItem key={index}>
                <InfoItemTitle>{infoItem.label}</InfoItemTitle>
                <InfoItemDescription>{infoItem.value}</InfoItemDescription>
              </InfoItem>
            ))}
        </InfoItemsContainer>

        {resolvedCtaLink && (
          <CtaWithArrow link={resolvedCtaLink} label={ctaLabel} darkTheme />
        )}
      </CaseStudiesSliderItemLeftContent>
      <CaseStudiesSliderItemRightContent>
        <ImageContainer to={resolvedCtaLink || '#'}>
          <LazyImage
            fluid={getFluidGatsbyImage(image)}
            width="100%"
            height="100%"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </ImageContainer>
      </CaseStudiesSliderItemRightContent>
    </CaseStudiesSliderItemContainer>
  );
}
